import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiToken } from '@couch/shared';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { CopyField } from '../../components/CopyField';
import { Field, FormError, useSubmit } from '../../components/Field';

export const API_TOKENS_KEY = ['api-tokens'] as const;
const KEY = API_TOKENS_KEY;
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'never');

function NewToken({ secret }: { secret: string }) {
  const command = `claude mcp add --transport http couch ${window.location.origin}/api/v1/mcp --header "Authorization: Bearer ${secret}"`;
  return (
    <div role="status" className="created-link">
      <p><strong>This token is shown only now.</strong> Use it as a bearer token, or add Couch to Claude Code:</p>
      <CopyField value={secret} label="API token" />
      <CopyField value={command} label="Claude Code command" />
    </div>
  );
}

function TokenRow({ token }: { token: ApiToken }) {
  const queryClient = useQueryClient();
  const revoke = useMutation({ mutationFn: () => api.revokeApiToken(token.id), onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }) });
  return (
    <tr>
      <td>{token.name}</td>
      <td>{when(token.createdAt)}</td>
      <td>{when(token.lastUsedAt)}</td>
      <td><button type="button" className="button-quiet" aria-label={`Revoke ${token.name}`} disabled={revoke.isPending} onClick={() => { revoke.mutate(); }}>Revoke</button></td>
    </tr>
  );
}

/** Personal tokens for scripts and AI clients (MCP). They reach machines, recipes and runs only. */
export function ApiTokensPanel() {
  const queryClient = useQueryClient();
  const tokens = useQuery({ queryKey: KEY, queryFn: api.apiTokens });
  const [name, setName] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const form = useSubmit(() => api.createApiToken(name), (created) => {
    setSecret(created.secret);
    setName('');
    void queryClient.invalidateQueries({ queryKey: KEY });
  });
  const list = tokens.data?.tokens ?? [];
  return (
    <section className="panel" aria-labelledby="api-tokens-title">
      <h2 id="api-tokens-title">API tokens</h2>
      <p className="muted">For scripts and AI assistants (MCP). A token reaches your machines, workflows and runs — never your account settings.</p>
      <form onSubmit={form.submit} noValidate className="invite-form">
        <Field label="Token name" name="name" value={name} onChange={setName} error={form.fieldErrors.name} hint="e.g. Claude Code on the MacBook" />
        <FormError message={form.formError} />
        <button type="submit" disabled={form.pending || name.trim() === ''}>Create token</button>
      </form>
      {secret && <NewToken secret={secret} />}
      {tokens.isError && <p role="alert" className="form-error">{errorMessage(tokens.error)}</p>}
      {list.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead><tr><th scope="col">Name</th><th scope="col">Created</th><th scope="col">Last used</th><th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
            <tbody>{list.map((t) => <TokenRow key={t.id} token={t} />)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}
